import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, ScrollText, CheckCircle2, XCircle } from "lucide-react";
import axios from "axios";
import { API } from "../../config";
import { useAuth } from "../../AuthContext";

/** McpAuditCard — MCP Server audit trail (tool calls + auth failures). */
export default function McpAuditCard({ dm }) {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ calls_today: 0, auth_failures_today: 0 });
  const [loading, setLoading] = useState(true);
  const { authHeader } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/mcp-audit?limit=50`, { headers: authHeader() });
      setEntries(data.entries || []);
      setStats({ calls_today: data.calls_today || 0, auth_failures_today: data.auth_failures_today || 0 });
    } catch {
      // silent — card shows empty state
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const card = `rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`;
  const cardShadow = { boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" };

  return (
    <div className={card} style={cardShadow} data-testid="mcp-audit-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className={`text-sm font-semibold flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
          <ScrollText className="w-4 h-4" /> MCP Audit Log
        </h3>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${dm ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`} data-testid="mcp-audit-calls-today">
            {stats.calls_today} calls today
          </span>
          {stats.auth_failures_today > 0 && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${dm ? "bg-red-400/10 text-red-400" : "bg-red-50 text-red-600"}`} data-testid="mcp-audit-failures-today">
              {stats.auth_failures_today} auth failures
            </span>
          )}
          <button
            onClick={load}
            data-testid="mcp-audit-refresh-btn"
            className={`p-1.5 rounded-lg transition-colors ${dm ? "text-slate-500 hover:bg-white/5" : "text-[#6b7a7d] hover:bg-slate-100"}`}
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <RefreshCw className={`w-4 h-4 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
        </div>
      ) : entries.length === 0 ? (
        <p className={`text-xs text-center py-6 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
          No MCP activity yet. Calls to /api/mcp and /api/mcp-admin will appear here.
        </p>
      ) : (
        <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
          {entries.map((e) => (
            <div
              key={e.id}
              data-testid={`mcp-audit-row-${e.id}`}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs ${dm ? "bg-[#0c1011]" : "bg-[#f3f4f5]"}`}
            >
              {e.ok ? (
                <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 text-emerald-500" />
              ) : (
                <XCircle className="w-3.5 h-3.5 flex-shrink-0 text-red-500" />
              )}
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase flex-shrink-0 ${
                e.endpoint === "mcp-admin"
                  ? (dm ? "bg-amber-400/15 text-amber-300" : "bg-amber-100 text-amber-700")
                  : (dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/10 text-[#006874]")
              }`}>
                {e.endpoint}
              </span>
              <code className={`font-mono font-semibold ${dm ? "text-slate-300" : "text-[#191c1d]"}`}>{e.tool}</code>
              <span className={`truncate flex-1 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                {e.key_name || e.key_prefix || "—"}{e.tier ? ` · ${e.tier}` : ""}
                {e.error_code ? ` · ${e.error_code}` : ""}
              </span>
              <span className={`flex-shrink-0 font-mono ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                {e.latency_ms != null ? `${e.latency_ms}ms` : ""}
              </span>
              <span className={`flex-shrink-0 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                {e.ts ? new Date(e.ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
